import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notificationApi } from '../api/endpoints.js';
import { useRealtimeEvent } from '../hooks/useRealtime.jsx';
import { formatDateTime, fullName } from '../utils/format.js';

/**
 * The bell in the header.
 *
 * A sales person asks IT a question in a customer thread and needs the answer
 * to come back without watching the page. So: a badge with the unread count,
 * refreshed on a timer, and one click that lands you on the conversation - the
 * notification is a pointer, the thread stays the single place things are said.
 *
 * It arrives live: the server pushes each notification down the same stream the
 * chat uses, so the badge moves while you are reading something else, with no
 * refresh. The slow poll stays underneath as a safety net - if the stream was
 * down while a notification was raised, the next tick picks it up.
 */
const POLL_MS = 60000;

const NotificationBell = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.resolvedLanguage;

  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const load = async () => {
    try {
      const data = await notificationApi.list({ limit: 20 });
      setItems(data.items ?? []);
      setUnread(data.unread ?? 0);
    } catch {
      // A failed poll is not worth a toast - the next one will pick it up.
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, []);

  // Pushed by the server the moment somebody writes to you.
  useRealtimeEvent('notification', (item) => {
    setItems((list) => (list.some((row) => row.id === item.id) ? list : [item, ...list].slice(0, 20)));
    setUnread((count) => count + 1);
  });

  // Click outside closes the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  /**
   * Where a notification points - the message or the comment itself, never just
   * the page containing it. `comment=` is what makes the thread scroll to the
   * right note and flash it; `task=` reopens the task it was written on.
   */
  const targetOf = (item) => {
    if (item.conversationId) return `/chat?c=${item.conversationId}`;
    if (item.taskId) {
      return `/tasks?task=${item.taskId}${item.commentId ? `&comment=${item.commentId}` : ''}`;
    }
    if (item.customerId) {
      return `/customers/${item.customerId}${item.commentId ? `?comment=${item.commentId}` : ''}`;
    }
    return null;
  };

  const openItem = async (item) => {
    setOpen(false);
    if (!item.read) {
      await notificationApi.markRead(item.id).catch(() => {});
      load();
    }
    const to = targetOf(item);
    if (to) navigate(to);
  };

  const readAll = async () => {
    await notificationApi.markAllRead().catch(() => {});
    load();
  };

  return (
    <div className="bell" ref={boxRef}>
      <button
        type="button"
        className="btn btn--ghost bell__btn"
        onClick={() => setOpen((value) => !value)}
        aria-label={t('notifications.title')}
      >
        <span aria-hidden="true">🔔</span>
        {unread > 0 ? <span className="bell__count">{unread > 99 ? '99+' : unread}</span> : null}
      </button>

      {open ? (
        <div className="bell__panel">
          <div className="bell__head">
            <span>{t('notifications.title')}</span>
            {unread > 0 ? (
              <button type="button" className="link-button" onClick={readAll}>
                {t('notifications.markAllRead')}
              </button>
            ) : null}
          </div>

          {!items.length ? (
            <p className="bell__empty muted">{t('notifications.empty')}</p>
          ) : (
            <ul className="bell__list">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={item.read ? 'bell__item' : 'bell__item bell__item--unread'}
                    onClick={() => openItem(item)}
                  >
                    <span className="bell__item-title">
                      {fullName(item.actor)}
                      {item.customerName ? ` · ${item.customerName}` : ''}
                    </span>
                    <span className="bell__item-body">{item.body}</span>
                    <span className="bell__item-date">{formatDateTime(item.createdAt, locale)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default NotificationBell;
